<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { RouterLink } from 'vue-router'

import BProgress from '../BProgress/BProgress.vue'

import { useDefaults, useTheme } from '../../composables'
import {
	getForeground,
	hexToHSLVars,
	isUiKitColor
} from '../../utils/color-utils'
import {
	BButtonEvents,
	BButtonProps,
	BButtonSlots,
	buttonSizeSetting,
	iconButtonSizeSetting
} from './use-button'

const BIcon = defineAsyncComponent(() => import('../BIcon/BIcon.vue'))

const _props = withDefaults(defineProps<BButtonProps>(), {
	color: 'primary',
	size: 'base',
	variant: 'filled',
	type: 'button'
})
const props = useDefaults(_props)
const emits = defineEmits<BButtonEvents>()
defineSlots<BButtonSlots>()

const componentTag = computed(() => {
	if (props.to) {
		return RouterLink
	}
	if (props.href) {
		return 'a'
	}
	return 'button'
})

const componentProps = computed(() => {
	if (props.to) {
		return {
			to: props.disabled ? undefined : props.to,
			replace: props.replace,
			exact: props.exact
		}
	} else if (props.href) {
		return {
			href: props.disabled ? undefined : props.href,
			target: props.target
		}
	}
	return {
		type: props.type
	}
})

const theme = useTheme()
const computedStyles = ref<Record<string, string>>({})

watch(
	[
		() => theme.theme.value,
		() => theme.isDark.value,
		() => props.color,
		() => props.textColor
	],
	() => {
		let buttonColor: string = props.color
		let textColor: string = ''

		if (isUiKitColor(props.color)) {
			buttonColor = getComputedStyle(document.documentElement)
				.getPropertyValue(`--color-${props.color}`)
				.trim()
			textColor = getComputedStyle(document.documentElement)
				.getPropertyValue(`--color-on-${props.color}`)
				.trim()
		}

		if (props.textColor) {
			if (isUiKitColor(props.textColor)) {
				textColor = getComputedStyle(document.documentElement)
					.getPropertyValue(`--color-on-${props.color}`)
					.trim()
			} else {
				textColor = props.textColor
			}
		}

		const { h, s, l } = hexToHSLVars(buttonColor)
		computedStyles.value = {
			'--btn-color-h': `${h}`,
			'--btn-color-s': `${s}%`,
			'--btn-color-l': `${l}%`,
			'--btn-foreground': textColor || getForeground(buttonColor),
			'--btn-color': buttonColor
		}
	},
	{ immediate: true }
)

const progressBarColor = computed(() => {
	switch (props.variant) {
		case 'filled':
			return computedStyles.value['--btn-foreground']
		case 'tonal':
		case 'outline':
		case 'link':
			return computedStyles.value['--btn-color']
		default:
			return computedStyles.value['--btn-foreground']
	}
})

const ariaLabel = computed(() => {
	if (props.text) {
		return props.text
	}
	if (props.icon) {
		return props.icon + ' icon button'
	}
	return 'Button'
})

const element = ref<HTMLButtonElement | HTMLAnchorElement | null>(null)
defineExpose({
	blur: () => {
		if (element.value && 'blur' in element.value) {
			element.value.blur()
		}
	}
})
</script>

<template>
	<component
		:is="componentTag"
		ref="element"
		v-bind="componentProps"
		:class="[
			'b-btn rounded-md inline-block relative',
			`b-btn--${props.variant}`,
			{ 'cursor-pointer': !props.disabled },
			{ 'cursor-not-allowed opacity-34 b-btn--disabled': props.disabled },
			{ 'w-full': props.block },
			{ 'cursor-progress loading-active b-button--loading': props.loading },
			{ [buttonSizeSetting[props.size]]: !props.icon }
		]"
		:style="computedStyles"
		:disabled="
			componentTag === 'button' ? props.disabled || props.loading : undefined
		"
		:aria-disabled="props.disabled"
		:aria-busy="props.loading || undefined"
		:aria-label="ariaLabel"
		@click="props.loading || emits('click', $event)"
	>
		<span class="b-button-content w-full">
			<span
				v-if="props.icon"
				:class="[
					'flex align-middle leading-[0]',
					iconButtonSizeSetting[props.size]
				]"
			>
				<BIcon
					:icon="props.icon"
					size="20"
					aria-hidden="true"
				/>
			</span>

			<span
				v-else
				:class="[
					'flex items-center justify-center',
					props.size === 'xs' || props.size === 'sm' ? 'gap-2' : 'gap-3'
				]"
			>
				<slot name="prepend">
					<BIcon
						v-if="props.prependIcon"
						:icon="props.prependIcon"
						size="20"
						aria-hidden="true"
					/>
				</slot>

				<slot>
					{{ props.text }}
				</slot>

				<slot name="append">
					<BIcon
						v-if="props.appendIcon"
						:icon="props.appendIcon"
						size="20"
						aria-hidden="true"
					/>
				</slot>
			</span>
		</span>

		<span
			v-if="props.loading"
			class="b-button-loading absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300 ease-in-out"
			aria-hidden="true"
		>
			<BProgress
				:color="progressBarColor"
				size="20"
				indeterminate
			/>
		</span>
	</component>
</template>

<style scoped>
.b-button-loading {
	opacity: 0;
}

.loading-active .b-button-content {
	opacity: 0;
}

.loading-active .b-button-loading {
	opacity: 1;
}

.b-btn {
	--btn-color: hsl(var(--btn-color-h), var(--btn-color-s), var(--btn-color-l));
	border: 1px solid transparent;
}

.b-btn--filled {
	background-color: var(--btn-color);
	color: var(--btn-foreground, --color-on-primary);
}

.b-btn--disabled {
	background-color: var(--btn-color) !important;
	color: var(--btn-foreground, --color-on-primary) !important;
}

[data-theme-mode='light'] {
	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) - 10%)
		);
	}

	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) - 15%)
		);
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) - 20%)
		);
	}
}

[data-theme-mode='dark'] {
	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) + 10%)
		);
	}

	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) + 15%)
		);
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--filled:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: hsl(
			var(--btn-color-h),
			var(--btn-color-s),
			calc(var(--btn-color-l) + 20%)
		);
	}
}

.b-btn--outline,
.b-btn--link {
	background-color: transparent;
	color: var(--btn-color, --color-on-primary);
}

.b-btn--outline {
	border-color: var(--btn-color);
}

[data-theme-mode='light'] {
	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 8%,
			transparent
		) !important;
	}

	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 12%,
			transparent
		) !important;
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 16%,
			transparent
		) !important;
	}
}

[data-theme-mode='dark'] {
	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 8%,
			transparent
		) !important;
	}

	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 12%,
			transparent
		) !important;
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--outline:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 16%,
			transparent
		) !important;
	}
}

[data-theme-mode='light'] {
	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 8%,
			transparent
		) !important;
	}

	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 12%,
			transparent
		) !important;
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 16%,
			transparent
		) !important;
	}
}

[data-theme-mode='dark'] {
	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 8%,
			transparent
		) !important;
	}

	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 12%,
			transparent
		) !important;
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}

	.b-btn--link:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: color-mix(
			in srgb,
			var(--btn-color) 16%,
			transparent
		) !important;
	}
}

.b-btn--tonal {
	background-color: hsla(
		var(--btn-color-h),
		var(--btn-color-s),
		var(--btn-color-l),
		0.12
	);
	color: var(--btn-color, var(--color-on-primary));
}

[data-theme-mode='light'] {
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.15
		);
	}
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.2
		);
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.25
		);
	}
}

[data-theme-mode='dark'] {
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):hover {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.15
		);
	}
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):focus,
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):focus-visible {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.2
		);
		outline: 2px solid color-mix(in srgb, var(--btn-color) 10%, transparent);
	}
	.b-btn--tonal:not(.b-btn--disabled):not(.b-button--loading):active {
		background-color: hsla(
			var(--btn-color-h),
			var(--btn-color-s),
			var(--btn-color-l),
			0.25
		);
	}
}
</style>
